/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import {
	STANDARD_EDITION,
	PREMIUM_EDITION,
	type ApplicationEdition,
} from '@jira/platform__application-editions/src/edition.tsx';
import { useRovoEntitlementResource } from '@jira/router-resources__rovo-entitlement/src/useRovoEntitlementResource.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { QueryParams } from '@atlassian/search-page/utils/query-params/types';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';

const ISSUE_NAVIGATOR_URL = `/issues/?jql=${encodeURIComponent(
	'created >= -30d order by created DESC',
)}`;

export const isQueryLikeWorkItemKey = (query: string): boolean =>
	/^[A-Z0-9]{2,}-\d+$/i.test(query.trim());

const buildJiraSearchUrl = (query: string) =>
	`/jira/search?searchString=${encodeURIComponent(query)}`;

const buildSmartQueryAdvancedSearchUrl = (query: string) =>
	`/secure/QuickSearch.jspa?searchString=${encodeURIComponent(query)}`;

const buildJQLAdvancedIssueUrl = (jql: string) => `/issues/?jql=${jql}`;

const buildTextAdvancedIssueUrl = (text: string) => {
	// the text has a value at this stage
	const jql =
		isQueryLikeWorkItemKey(text) && fg('rovo_search_update_work_items_link')
			? `issuekey ~ "${encodeURIComponent(text)}*"`
			: `textfields ~ "${encodeURIComponent(text)}*"`;

	return `/issues/?jql=${jql}`;
};

// export for testing
export const buildJiraAdvancedSearchPath = ({
	query,
	jql,
	issueKeyLookup = true,
}: {
	query: string;
	jql?: string;
	issueKeyLookup?: boolean;
}): string => {
	const trimmedQuery = query.trim();
	if (!trimmedQuery) {
		// If query is empty, we utilise a spa transition with ISSUE_NAVIGATOR_URL
		return query === '' ? ISSUE_NAVIGATOR_URL : buildSmartQueryAdvancedSearchUrl(query);
	}

	if (jql) {
		// if JQL is provided, we build the work items URL with JQL
		return buildJQLAdvancedIssueUrl(jql);
	}

	if (issueKeyLookup) {
		// we navigate to the search page to handle the single work item page in case the query is a work item key
		return buildJiraSearchUrl(trimmedQuery);
	}

	return buildTextAdvancedIssueUrl(trimmedQuery);
};

export const buildJiraAdvancedSearchUrl = ({
	query,
	jql,
	siteUrl,
	issueKeyLookup,
}: {
	query: string;
	siteUrl: string;
	jql?: string;
	issueKeyLookup?: boolean;
}): URL => {
	return new URL(buildJiraAdvancedSearchPath({ query, jql, issueKeyLookup }), siteUrl);
};

export const useRovoSearchEnabled = (): boolean | null | undefined => {
	const isAnonymous = useIsAnonymous();
	const appEditions = useAppEditions();
	const rovoEntitlement = useRovoEntitlementResource();

	// Return false for anonymous users
	if (isAnonymous) {
		return false;
	}

	/**
	 * Check if JSW or JSM has valid edition (standard or premium)
	 *
	 * We do this as Rovo in Jira is tied to content being indexed by the Search Platform,
	 * which is only done for tenants which have standard, premium and enterprise editions
	 * of Jira Software OR Jira Service Management.
	 * Ref:
	 * - https://bitbucket.org/atlassian/xpsearch-content/src/a0344602bf66ed47e3b71414fe873651988cff84/xpsearch-content-indexer/src/main/java/io/atlassian/xpsearch/content/indexer/service/event/tenant/ContentReindexingTenantLifecycleHandler.java#lines-178
	 * - https://bitbucket.org/atlassian/xpsearch-content/src/a0344602bf66ed47e3b71414fe873651988cff84/xpsearch-content-indexer/src/main/java/io/atlassian/xpsearch/content/indexer/service/event/tenant/ContentReindexingTenantLifecycleHandler.java#lines-346:354
	 *
	 * Note that we only check for STANDARD_EDITION and PREMIUM_EDITION as enterprise
	 * edition gets mapped to PREMIUM_EDITION. This was confirmed by checking the TCS record for Hello
	 * - XPSearchTransformer: jira-software.ondemand is enterprise
	 * - JiraBifrostTransformer: productEditions['jira-software'] is 'p', i.e. premium
	 * https://snoopr.prod.atl-paas.net/ts?zone=prod&entity_type=CLOUD&key-type=AUTO_GUESS&s=a436116f-02ce-4520-8fbb-7301462a1674
	 */
	const isValidEdition = (edition: ApplicationEdition): boolean =>
		edition === STANDARD_EDITION || edition === PREMIUM_EDITION;
	const hasValidJSWEdition = isValidEdition(appEditions?.software);
	const hasValidJSMEdition = isValidEdition(appEditions?.serviceDesk);

	// Return false if neither JSW nor JSM has a valid edition
	if (!hasValidJSWEdition && !hasValidJSMEdition) {
		return false;
	}

	return rovoEntitlement?.data?.isRovoEnabled && fg('rovo_search_jira_enabled');
};

const buildRovoSearchUrl = (siteUrl?: string, queryString?: string) => {
	const url = new URL('/jira/rovo-search', siteUrl || '');
	url.searchParams.append('text', queryString || '');
	return url;
};

// TODO: This should be replaced by generateJiraSearchUrl from search-common package (The SSR-friendly version of the same function)
export const generateSearchUrl = (
	urlQueryParams: QueryParams,
	isSecondaryLink?: boolean,
	origin?: string,
	search?: string,
	issueKeyLookup?: boolean,
): string => {
	const { jql = '', text: queryString = '', ...rest } = urlQueryParams;
	// Suppress warning of `window` usage. This code is never run on the server so this usage is safe
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	const siteOrigin = origin ?? window.location.origin;
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	const siteSearch = search ?? window.location.search;

	const url = isSecondaryLink
		? buildJiraAdvancedSearchUrl({
				query: queryString?.toString() || '',
				siteUrl: siteOrigin,
				jql: jql?.toString() || '',
				issueKeyLookup,
			})
		: buildRovoSearchUrl(siteOrigin, queryString?.toString());

	// Default - Set search params for rovo search link only
	if (!isSecondaryLink) {
		const searchParams = new URLSearchParams(siteSearch);
		Object.entries(urlQueryParams).forEach(([key, value]) => {
			if (value !== undefined && value !== null) {
				searchParams.set(key, String(value));
			}
		});

		url.search = searchParams.toString();
	} else {
		// while navigating to NIN we need to a few query parameters to preserve the context of the session.
		Object.entries(rest).forEach(([key, value]) => {
			if (value !== undefined && value !== null) {
				url.searchParams.set(key, String(value));
			}
		});
	}

	return url.href;
};
